import { useState } from 'react';
import { CircularProgress, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { formatCep } from '@dts/shared';

export interface LookupViaCepButtonProps {
  /** Valor controlado do CEP (8 dígitos ou mascarado — a apresentação aplica `formatCep`). */
  value: string;
  /** Atualiza o valor enquanto o usuário digita. */
  onChange: (cep: string) => void;
  /**
   * Dispara a busca. Recebe o CEP em dígitos puros (8 chars). O caller resolve
   * a chamada server-side e pode preencher os outros campos do form.
   * Erros não são tratados aqui — o caller exibe mensagem (ex.: toast).
   */
  onLookup: (cepDigits: string) => Promise<void>;
  /** Em flight do lookup — desabilita botão e mostra spinner. */
  loading?: boolean;
  /** Mensagem de erro de validação a exibir no helperText. */
  error?: string;
  /** Desabilita TextField + botão. */
  disabled?: boolean;
  /** Label da TextField (default i18n `forms.cep.label`). */
  label?: string;
  /** id/name para integração com react-hook-form (se houver). */
  id?: string;
  name?: string;
  onBlur?: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
}

/**
 * TextField de CEP com botão de busca ViaCEP integrado. Quando o usuário clica
 * na lupa (ou pressiona Enter), dispara `onLookup(cepDigits)` se houver 8 dígitos.
 *
 * Apresentação: o valor é exibido mascarado (`00000-000`) via `formatCep` de
 * `@dts/shared`; o `onChange` continua emitindo o que foi digitado (raw).
 */
export function LookupViaCepButton({
  value,
  onChange,
  onLookup,
  loading = false,
  error,
  disabled = false,
  label,
  id,
  name,
  onBlur,
  inputRef,
}: LookupViaCepButtonProps): JSX.Element {
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);
  const digits = value.replace(/\D/g, '');
  const canLookup = digits.length === 8 && !loading && !disabled;
  const display = focused ? value : formatCep(value);

  const trigger = async (): Promise<void> => {
    if (!canLookup) return;
    await onLookup(digits);
  };

  return (
    <TextField
      id={id}
      name={name}
      label={label ?? t('forms.cep.label')}
      value={display}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        onBlur?.();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          void trigger();
        }
      }}
      error={Boolean(error)}
      helperText={error ?? t('forms.cep.helper')}
      disabled={disabled}
      inputProps={{ maxLength: 9, inputMode: 'numeric' }}
      inputRef={inputRef}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <Tooltip title={t('forms.cep.lookupTooltip')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => void trigger()}
                    disabled={!canLookup}
                    aria-label={t('forms.cep.lookupAria')}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </InputAdornment>
        ),
      }}
      sx={{ maxWidth: 220 }}
    />
  );
}
